import type { MetaFunction } from '@remix-run/node';
import type { loader as professionalsLoader } from '~/routes/_app.professionals.$';
import type { loader as professionalsLoaderRocco } from '~/routes/_app.rocco.professionals.$';
import type { ListItem, LocalBusiness, Organization } from 'schema-dts';
import { getMetaBaseUrl } from '../util/create-meta-base-url';
import type { StaticParentLoaders } from '../util';
import {
    buildAreaServed,
    buildGeo,
    buildPostalAddress,
    cleanString,
    pickProfessionalType,
    selectPrimaryPremises,
} from '../util/professional-location-schema';

export const createProfessionalsItemListSchema: MetaFunction<
    typeof professionalsLoader | typeof professionalsLoaderRocco,
    StaticParentLoaders
> = (props) => {
    const { data, location } = props;
    if (!data) {
        return [];
    }
    const baseUrl = getMetaBaseUrl(props);

    // The applied location (if the page is a category x location page) lets us surface the
    // premises relevant to that location in each professional's schema. `location-*` path
    // segments are resolved in the loader (the slug can't be parsed from the URL alone), so we
    // read the resolved location from loader data rather than re-parsing the pathname.
    const appliedLocationFilter =
        'appliedLocation' in data ? data.appliedLocation : undefined;

    const itemListElement: ListItem[] =
        data.searchResult?.hits?.map((prof, idx) => {
            const premises = selectPrimaryPremises(
                prof.SearchLocations,
                appliedLocationFilter,
            );
            const geo = buildGeo(premises?.MapCoordinates);
            const areaServed = buildAreaServed(prof.SearchLocations);
            const telephone = cleanString(prof.Phone);
            const email = cleanString(prof.Email);

            const item: LocalBusiness | Organization = {
                '@type': pickProfessionalType(premises),
                '@id': baseUrl + prof.Link,
                name: prof.Title ?? '',
                logo: baseUrl + prof.CompanyLogos?.Thumb_sm,
                image: baseUrl + prof.Thumb_lg,
                url: baseUrl + prof.Link,
                ...(telephone && { telephone }),
                ...(email && { email }),
                ...(premises && {
                    address: buildPostalAddress(premises, prof.Country),
                }),
                ...(geo && { geo }),
                ...(areaServed.length > 0 && { areaServed }),
            };

            return {
                '@type': 'ListItem',
                position: (data.pagination?.currentRange?.from ?? 1) + idx,
                item,
            };
        }) ?? [];

    return [
        {
            '@type': 'ItemList',
            url: baseUrl + location.pathname,
            numberOfItems: data.searchResult?.nbHits ?? 0,
            itemListElement,
        },
    ];
};
